import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, RouteComponentProps, RouteProps, Switch, useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { LoadingContainer } from "go-core/components/Loading";
import { partnerApi } from "../../../../../../services/Api/Partner/partnerApi";
import { PartnerActionTypes } from "../../../../../../services/Api/Partner/partnerTypes";
import { selectPartner } from "../../../../../../services/partner/selectors";
import { useUserPermissions } from "../../hooks";
import PartnerNavigation from "./components/PartnerNavigation";
import PartnerPreviewEditPage from "./pages/Edit";
import PartnerPreviewIndexPage from "./pages/Index";
import PartnerPreviewLoansPage from "./pages/Loans";
import PartnerPreviewOrganizationsPage from "./pages/Organizations";
import PartnerPreviewPartnersPage from "./pages/Partners";
import PartnerPreviewPayoffPage from "./pages/Payoff";
import PartnerPreviewPayoutInvoicesPage from "./pages/PayoutInvoices";
import PartnerPreviewReportPage from "./pages/Report";
import PartnerPreviewUsersPage from "./pages/Users";

interface MatchParams {
	partnerId: string;
}

const PartnerPreviewPage = (props: RouteComponentProps) => {
	const { addFlash } = useFlash();
	const partner = useSelector(selectPartner);
	const { hasBasicPermission, hasMoneyFlowPermission, hasUsersPermission } = useUserPermissions(
		partner?.current_user_permissions
	);
	const dispatch = useDispatch();
	const { partnerId } = useParams<MatchParams>();

	useEffect(() => {
		fetchPartner();
	}, []);

	const fetchPartner = async () => {
		try {
			const res = await partnerApi.getPartner(Number(partnerId), { include: "parent,any_sub_partner" });
			dispatch({
				type: PartnerActionTypes.SET,
				payload: res,
			});
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const routes = useMemo<(RouteProps & { isHidden: boolean })[]>(
		() => [
			{
				exact: true,
				path: `${props.match.url}/`,
				component: PartnerPreviewIndexPage,
				isHidden: !hasBasicPermission,
			},
			{
				path: `${props.match.url}/organizations`,
				component: PartnerPreviewOrganizationsPage,
				isHidden: !hasBasicPermission,
			},
			{
				path: `${props.match.url}/loans`,
				component: PartnerPreviewLoansPage,
				isHidden: !hasBasicPermission,
			},
			{
				path: `${props.match.url}/users`,
				component: PartnerPreviewUsersPage,
				isHidden: !hasUsersPermission,
			},
			{
				path: `${props.match.url}/payout_invoices`,
				component: PartnerPreviewPayoutInvoicesPage,
				isHidden: !hasMoneyFlowPermission,
			},
			{
				path: `${props.match.url}/payoff`,
				component: PartnerPreviewPayoffPage,
				isHidden: !hasMoneyFlowPermission,
			},
			{
				path: `${props.match.url}/report`,
				component: PartnerPreviewReportPage,
				isHidden: !hasMoneyFlowPermission,
			},
			{
				path: `${props.match.url}/edit`,
				component: PartnerPreviewEditPage,
				isHidden: false,
			},
			{
				path: `${props.match.url}/partners`,
				component: PartnerPreviewPartnersPage,
				isHidden: false,
			},
		],
		[hasBasicPermission, hasMoneyFlowPermission, hasUsersPermission, props.match.url]
	);

	if (!partner) return <LoadingContainer />;

	return (
		<div className={"page-container"}>
			<PartnerNavigation history={props.history} location={props.location} match={props.match} />
			<div className={"content"}>
				<Switch>
					{routes
						.filter((route) => !route.isHidden)
						.map((route, index) => (
							<Route key={index} {...route} />
						))}
				</Switch>
			</div>
		</div>
	);
};
export default PartnerPreviewPage;
